import React, { useState, useEffect, useRef, useMemo } from 'react';
import { db } from '../../config/firebase';
import { collection, addDoc, getDocs } from 'firebase/firestore';
import './NewBusiness.css';
import { motion } from 'framer-motion';

const formatTime = (time) => {
  const [hours, minutes] = time.split(':');
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour = hours % 12 || 12;
  return `${hour}:${minutes} ${period}`;
};

const TimeSelector = ({ value, onChange, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const timeOptions = useMemo(() => {
    const times = [];
    for (let i = 0; i < 24; i++) {
      for (let j = 0; j < 60; j += 30) {
        const hour = i.toString().padStart(2, '0');
        const minute = j.toString().padStart(2, '0');
        const timeString = `${hour}:${minute}`;
        const formattedTime = formatTime(timeString); // Convert to 12-hour format
        times.push({ value: timeString, label: formattedTime });
      }
    }
    return times;
  }, []);

  const dropdownRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    // Scroll to selected time when dropdown opens
    if (isOpen && listRef.current) {
      const selectedElement = listRef.current.querySelector('.selected');
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="time-selector" ref={dropdownRef}>
      <div 
        className={`time-display ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <i className="far fa-clock"></i>
        <span className="time-text">{formatTime(value)}</span>
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
      </div>
      
      {isOpen && (
        <div className="time-dropdown">
          <div className="time-list" ref={listRef}>
            {timeOptions.map((time) => (
              <div
                key={time.value}
                className={`time-option ${time.value === value ? 'selected' : ''}`}
                onClick={() => {
                  onChange(time.value);
                  setIsOpen(false);
                }}
              >
                {time.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const StatusOverlay = ({ message, type, onClose }) => {
  return (
    <motion.div 
      className="mobile-status-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <div className="mobile-status-content">
        <div className={`status-icon ${type}`}>
          {type === 'success' ? (
            <i className="fas fa-check-circle"></i>
          ) : (
            <i className="fas fa-exclamation-circle"></i>
          )}
        </div>
        <p>{message}</p>
        <small>Tap anywhere to close</small>
      </div>
    </motion.div>
  );
};

const NewBusiness = () => {
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    businessName: '',
    category: '',
    newCategory: '',
    phone: '',
    email: '',
    address: '',
    location: '',
    businessHours: '',
    description: ''
  });
  const [submitStatus, setSubmitStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [businessHours, setBusinessHours] = useState({
    monday: { isOpen: false, open: '09:00', close: '17:00' },
    tuesday: { isOpen: false, open: '09:00', close: '17:00' },
    wednesday: { isOpen: false, open: '09:00', close: '17:00' },
    thursday: { isOpen: false, open: '09:00', close: '17:00' },
    friday: { isOpen: false, open: '09:00', close: '17:00' },
    saturday: { isOpen: false, open: '09:00', close: '17:00' },
    sunday: { isOpen: false, open: '09:00', close: '17:00' }
  });
  const [showStatusOverlay, setShowStatusOverlay] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      const snapshot = await getDocs(collection(db, 'providers'));
      const uniqueCategories = [...new Set(snapshot.docs.map(doc => doc.data().category))];
      setCategories(uniqueCategories.sort());
    };
    fetchCategories();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('');

    try {
      if (!formData.businessName || !formData.phone || !formData.email) {
        setSubmitStatus('Please fill in all required fields.');
        setShowStatusOverlay(true);
        return;
      }

      const finalCategory = formData.category === 'Other' ? formData.newCategory : formData.category;
      
      // Format the phone number before sending
      const formattedPhone = formData.phone.replace(/\D/g, '');
      
      const notificationData = {
        ...formData,
        phoneNumber: formattedPhone, // Add phoneNumber field for notifications
        phone: formattedPhone, // Keep phone field for backward compatibility
        category: finalCategory,
        status: 'pending',
        timestamp: new Date(),
        type: 'business',
        notificationType: 'New Business Registration',
        viewed: false,
        businessHours: businessHours
      };

      const docRef = await addDoc(collection(db, 'businessRequests'), notificationData);

      setSubmitStatus('Successfully submitted! We will review your application shortly.');
      setShowStatusOverlay(true);
      
      // Reset form after delay
      setTimeout(() => {
        setFormData({
          businessName: '',
          category: '',
          newCategory: '',
          phone: '',
          email: '',
          address: '',
          location: '',
          businessHours: '',
          description: ''
        });
        setSearchTerm('');
      }, 3000);

    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus(`Error submitting form: ${error.message}`);
      setShowStatusOverlay(true);
    }
  };

  const handleCategoryInput = (e) => {
    const input = e.target.value;
    setSearchTerm(input);
    setShowDropdown(true);

    const matchingCategory = categories.find(
      category => category.toLowerCase() === input.toLowerCase()
    );

    if (matchingCategory) {
      setFormData(prev => ({
        ...prev,
        category: matchingCategory,
        newCategory: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        category: 'Other',
        newCategory: input
      }));
    }
  };

  const handleCategorySelect = (category) => {
    setSearchTerm(category);
    setFormData(prev => ({
      ...prev,
      category: category,
      newCategory: ''
    }));
    setShowDropdown(false);
  };

  const handleHoursChange = (day, field, value) => {
    setBusinessHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
  };

  const daysOfWeek = [
    { id: 'monday', label: 'Monday' },
    { id: 'tuesday', label: 'Tuesday' },
    { id: 'wednesday', label: 'Wednesday' },
    { id: 'thursday', label: 'Thursday' },
    { id: 'friday', label: 'Friday' },
    { id: 'saturday', label: 'Saturday' },
    { id: 'sunday', label: 'Sunday' }
  ];

  return (
    <div className="new-business">
      <h1>Grow Your Business with AroundU</h1>
      
      <div className="feature-cards">
        <div className="feature-card">
          <div className="icon">
            <i className="fas fa-star" style={{ color: '#f0a500' }}></i>
          </div>
          <h3>Build Trust</h3>
          <p>Enhance your reputation through verified reviews</p>
        </div>
        
        <div className="feature-card">
          <div className="icon">
            <i className="fas fa-users" style={{ color: '#f0a500' }}></i>
          </div>
          <h3>Reach More Customers</h3>
          <p>Connect with local customers actively searching for services</p>
        </div>
        
        <div className="feature-card">
          <div className="icon">
            <i className="fas fa-chart-line" style={{ color: '#f0a500' }}></i>
          </div>
          <h3>Grow Your Business</h3>
          <p>Increase visibility and customer base</p>
        </div>
      </div>

      <div className="benefits-list">
        <div className="benefit-item">
          <div className="benefit-icon">
            <i className="fas fa-check-circle" style={{ color: '#00C853' }}></i>
          </div>
          <div className="benefit-content">
            <h4>Quality Reviews Drive Growth</h4>
            <p>Great service leads to positive reviews, building trust and attracting more customers</p>
          </div>
        </div>

        <div className="benefit-item">
          <div className="benefit-icon">
            <i className="fas fa-bullseye" style={{ color: '#00C853' }}></i>
          </div>
          <div className="benefit-content">
            <h4>Target Local Customers</h4>
            <p>Connect with customers in your area who are actively looking for your services</p>
          </div>
        </div>

        <div className="benefit-item">
          <div className="benefit-icon">
            <i className="fas fa-comments" style={{ color: '#00C853' }}></i>
          </div>
          <div className="benefit-content">
            <h4>Engage With Your Community</h4>
            <p>Respond to reviews and build lasting relationships with your customers</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="business-form">
        <h2>Register Your Business</h2>
        
        <div className="form-group">
          <label>Business Name</label>
          <input
            type="text"
            value={formData.businessName}
            onChange={(e) => setFormData({...formData, businessName: e.target.value})}
            required
          />
        </div>

        <div className="form-group">
          <label>Category</label>
          <div className="category-input-container">
            <input
              type="text"
              className="category-input"
              placeholder="Type or select a category"
              value={searchTerm}
              onChange={handleCategoryInput}
              onFocus={() => setShowDropdown(true)}
              required
            />
            
            {showDropdown && (
              <div className="category-dropdown">
                <div
                  className="category-option"
                  onClick={() => {
                    setSearchTerm('Other');
                    setFormData(prev => ({
                      ...prev,
                      category: 'Other',
                      newCategory: ''
                    }));
                    setShowDropdown(false);
                  }}
                >
                  Other
                </div>
                {categories
                  .filter(category => 
                    category.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map((category) => (
                    <div
                      key={category}
                      className="category-option"
                      onClick={() => handleCategorySelect(category)}
                    >
                      {category}
                    </div>
                  ))}
              </div>
            )}
          </div>

          {formData.category === 'Other' && (
            <div className="new-category-container">
              <input
                type="text"
                className="new-category-input"
                placeholder="Enter new category"
                value={formData.newCategory}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  newCategory: e.target.value
                }))}
                required
              />
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Phone Number</label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            required
          />
        </div>

        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            required
          />
        </div>

        <div className="form-group">
          <label>Address</label>
          <input
            type="text"
            value={formData.address}
            onChange={(e) => setFormData({...formData, address: e.target.value})}
            required
          />
        </div>

        <div className="form-group">
          <label>Location/Area</label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => setFormData({...formData, location: e.target.value})}
            placeholder="e.g., Sandown, Sandton"
            required
          />
        </div>

        <div className="form-group business-hours-group">
          <label>Business Hours</label>
          <div className="business-hours-container">
            {daysOfWeek.map(({ id, label }) => (
              <div key={id} className="day-row">
                <div className="day-toggle">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={businessHours[id].isOpen}
                      onChange={(e) => handleHoursChange(id, 'isOpen', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className="day-label">{label}</span>
                </div>
                
                {businessHours[id].isOpen && (
                  <div className="hours-inputs">
                    <div className="time-input-group">
                      <label>Opens</label>
                      <TimeSelector
                        value={businessHours[id].open}
                        onChange={(time) => handleHoursChange(id, 'open', time)}
                      />
                    </div>
                    <span className="time-separator">to</span>
                    <div className="time-input-group">
                      <label>Closes</label>
                      <TimeSelector
                        value={businessHours[id].close}
                        onChange={(time) => handleHoursChange(id, 'close', time)}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            required
          />
        </div>

        <button type="submit" className="submit-btn">Submit for Review</button>

        {submitStatus && (
          <div className={`submit-status ${submitStatus.includes('Error') || submitStatus.includes('Please fill') 
            ? 'error' 
            : 'success'}`}>
            {submitStatus}
          </div>
        )}
      </form>

      {showStatusOverlay && (
        <StatusOverlay 
          message={submitStatus}
          type={submitStatus.toLowerCase().includes('successfully') ? 'success' : 'error'}
          onClose={() => setShowStatusOverlay(false)}
        />
      )}
    </div>
  );
};

export default NewBusiness; 